import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Collapse,
  FormControl,
  HStack,
  Icon,
  IconButton,
  Input,
  SimpleGrid,
  Text,
  Textarea,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import {
  FaChevronDown,
  FaChevronUp,
  FaLock,
  FaUnlock,
  FaUndo,
  FaCheck,
  FaBell,
  FaTimes,
} from "react-icons/fa";
import {
  LuBarChart2,
  LuCalendarDays,
  LuInfo,
} from "react-icons/lu";
import lodash from "lodash";
import { ScenarioAnalysis } from "build-ts/analyst";
import { FilterProto } from "build-ts/metadata";
import { IoAnalytics } from "react-icons/io5";
import { MdOutlineIntegrationInstructions, MdSchedule } from "react-icons/md";
import { PiPresentationFill } from "react-icons/pi";

type TabId = "analysis" | "notes" | "schedule";

interface ScenarioArtifactCardProps {
  /** The scenario data from the artifact */
  scenario: ScenarioAnalysis;
  /** Whether this scenario is already saved in the current analyst */
  isInCurrentAnalyst: boolean;
  /** Whether this is the most-recently selected scenario */
  isSelected?: boolean;
  /** Called when user saves the form – receives fully-assembled scenario with preset_filters */
  onSave: (scenario: ScenarioAnalysis) => Promise<void>;
  /** Called when user removes the scenario artifact card */
  onRemove: () => void;
  /** Start expanded (e.g. most-recently arrived) */
  defaultExpanded?: boolean;
}

const ScenarioArtifactCard: React.FC<ScenarioArtifactCardProps> = ({
  scenario: initialScenario,
  isInCurrentAnalyst,
  isSelected = false,
  onSave,
  onRemove,
  defaultExpanded = true,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [activeTab, setActiveTab] = useState<TabId>("analysis");
  const [notesLocked, setNotesLocked] = useState(true);

  // Form state — all managed locally, initialised once on mount / when scenario id changes
  const [issue, setIssue] = useState(initialScenario.issue ?? "");
  const [trigger, setTrigger] = useState(initialScenario.trigger ?? "");
  const [reportingInstructions, setReportingInstructions] = useState(
    initialScenario.reporting_instructions ?? ""
  );
  const [analysisInstructions, setAnalysisInstructions] = useState(
    initialScenario.analysis_instructions ?? ""
  );
  const [bulkPresetFiltersMap, setBulkPresetFiltersMap] = useState<Map<string, FilterProto[]>>(() => {
    if (initialScenario.preset_filters && initialScenario.preset_filters.length > 0) {
      return new Map([["1", initialScenario.preset_filters]]);
    }
    return new Map();
  });

  const [isSaving, setIsSaving] = useState(false);

  const currentFiltersArray = Array.from(bulkPresetFiltersMap.values())[0] ?? [];
  const originalFiltersArray = initialScenario.preset_filters ?? [];
  const isChanged =
    issue !== (initialScenario.issue ?? "") ||
    trigger !== (initialScenario.trigger ?? "") ||
    reportingInstructions !== (initialScenario.reporting_instructions ?? "") ||
    analysisInstructions !== (initialScenario.analysis_instructions ?? "") ||
    !lodash.isEqual(currentFiltersArray, originalFiltersArray);

  useEffect(() => {
    setIssue(initialScenario.issue ?? "");
    setTrigger(initialScenario.trigger ?? "");
    setReportingInstructions(initialScenario.reporting_instructions ?? "");
    setAnalysisInstructions(initialScenario.analysis_instructions ?? "");
    if (initialScenario.preset_filters && initialScenario.preset_filters.length > 0) {
      setBulkPresetFiltersMap(new Map([["1", initialScenario.preset_filters]]));
    } else {
      setBulkPresetFiltersMap(new Map());
    }
  }, [initialScenario]);

  const displayLabel = issue?.trim() || `Scenario ${initialScenario.id ?? "?"}`;

  const handleFieldChange = <T extends string>(
    setter: React.Dispatch<React.SetStateAction<T>>
  ) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setter(e.target.value as T);
  };

  const handleSave = async () => {
    const presetFilters = Array.from(bulkPresetFiltersMap.values())[0] ?? [];
    const toSave: ScenarioAnalysis = {
      ...initialScenario,
      issue,
      trigger,
      reporting_instructions: reportingInstructions,
      analysis_instructions: analysisInstructions,
      preset_filters: presetFilters,
    };
    setIsSaving(true);
    try {
      await onSave(toSave);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setIssue(initialScenario.issue ?? "");
    setTrigger(initialScenario.trigger ?? "");
    setReportingInstructions(initialScenario.reporting_instructions ?? "");
    setAnalysisInstructions(initialScenario.analysis_instructions ?? "");
    if (initialScenario.preset_filters && initialScenario.preset_filters.length > 0) {
      setBulkPresetFiltersMap(new Map([["1", initialScenario.preset_filters]]));
    } else {
      setBulkPresetFiltersMap(new Map());
    }
  };

  const tabs: { id: TabId; label: string; icon: React.ElementType }[] = [
    { id: "analysis", label: "Analysis", icon: LuBarChart2 },
    { id: "schedule", label: "Schedule", icon: MdSchedule },
    { id: "notes", label: "Instructions", icon: MdOutlineIntegrationInstructions },
  ];

  return (
    <Box
      width="100%"
      bg="white"
      overflow="hidden"
    >
      {/* ── Card Header ─────────────────────────────────── */}
      <HStack
        px={2}
        py={1}
        bg={isSelected ? "purple.50" : "gray.50"}
        cursor="pointer"
        onClick={() => setIsExpanded((prev) => !prev)}
        justifyContent="space-between"
        borderBottom={isExpanded ? "1px solid" : "none"}
        borderColor="gray.200"
        transition="background 0.15s"
      >
        <HStack spacing={2} flex={1} minW={0}>
          <Icon as={IoAnalytics} boxSize={6} color="purple.600" flexShrink={0} />
          <Text
            fontSize="sm"
            fontWeight="600"
            color="gray.800"
            isTruncated
            mb={0}
            maxW="calc(100% - 60px)"
          >
            {displayLabel}
          </Text>

          {/* Saved / Draft badge */}
          {isInCurrentAnalyst ? (
            <Text
              fontSize="2xs"
              color="green.800"
              bg="green.100"
              px={2}
              py={0.5}
              borderRadius="sm"
              fontWeight="700"
              flexShrink={0}
              mb={0}
              border="1px solid"
              borderColor="green.200"
            >
              SAVED
            </Text>
          ) : (
            <Text
              fontSize="2xs"
              color="orange.700"
              bg="yellow.100"
              px={2}
              py={0.5}
              borderRadius="sm"
              fontWeight="700"
              flexShrink={0}
              mb={0}
              border="1px solid"
              borderColor="yellow.500"
            >
              DRAFT
            </Text>
          )}
        </HStack>
        <IconButton
          size="xs"
          variant="ghost"
          icon={<Icon as={FaTimes} boxSize={4} />}
          color="red.400"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Close"
        />
        <Icon
          as={isExpanded ? FaChevronUp : FaChevronDown}
          boxSize={3}
          color="gray.600"
          mr={1}
        />
      </HStack>

      {/* ── Expanded Body ───────────────────────────── */}
      <Collapse in={isExpanded} animateOpacity>
        <Box>
          {/* ── Tab Bar ── */}
          <HStack
            spacing={0}
            borderBottom="1px solid"
            borderColor="gray.200"
            bg="gray.50"
            px={2}
          >
            {tabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <HStack
                  key={tab.id}
                  spacing={1.5}
                  px={3}
                  py={1.5}
                  cursor="pointer"
                  borderBottom="2px solid"
                  borderColor={isActive ? "purple.500" : "transparent"}
                  color={isActive ? "purple.600" : "gray.500"}
                  _hover={{ color: isActive ? "purple.600" : "gray.700" }}
                  transition="all 0.15s"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab(tab.id);
                  }}
                  mb="-1px"
                >
                  <Icon as={tab.icon} boxSize={4} />
                  <Text fontSize="xs" fontWeight={isActive ? "700" : "500"} mb={0}>
                    {tab.label}
                  </Text>
                </HStack>
              );
            })}
          </HStack>

          {/* ── Tab Content ── */}
          <Box
            px={0}
            pt={0}
            pb={0}
            overflowY="auto"
            maxH="520px"
            sx={{
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: "#CBD5E0", borderRadius: "2px" },
            }}
          >
            {/* ── ANALYSIS TAB ── */}
            {activeTab === "analysis" && (
              <VStack spacing={2.5} align="stretch" p={2}>
                {/* Name / Issue – label + input inline */}
                <HStack spacing={2} align="center">
                  <Text fontSize="xs" fontWeight="600" color="gray.700" mb={0} whiteSpace="nowrap" flexShrink={0}>
                    Name
                  </Text>
                  <Input
                    size="sm"
                    value={issue}
                    onChange={handleFieldChange(setIssue)}
                    placeholder="e.g. Product sales spike vs 3-day avg…"
                    _focus={{
                      borderColor: "purple.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                    }}
                    fontWeight="600"
                    fontSize="xs"
                    flex={1}
                  />
                </HStack>

                {/* Trigger + Reporting side by side, 7 rows */}
                <SimpleGrid columns={2} spacing={2} px={1}>
                  <FormControl>
                    <HStack spacing={1} mb={1} align="center">
                      <Icon as={FaBell} boxSize={3.5} color="purple.700" />
                      <Text fontSize="xs" fontWeight="700" color="gray.700" mb={0}>
                        Analysis Trigger
                      </Text>
                      <Tooltip
                        label="When should this alert fire? Define the metric thresholds and data conditions."
                        placement="top"
                        hasArrow
                        fontSize="xs"
                        bg="blue.50"
                        color="gray.700"
                        py={2}
                        px={2}
                        borderRadius="md"
                        fontWeight="600"
                      >
                        <span>
                          <Icon as={LuInfo} boxSize={4} color="blue.300" cursor="help" />
                        </span>
                      </Tooltip>
                    </HStack>
                    <Textarea
                      size="sm"
                      value={trigger}
                      onChange={handleFieldChange(setTrigger)}
                      placeholder="e.g. CPL_today > 100 AND CPL_3day_avg < 50, spend > 0…"
                      rows={9}
                      resize="none"
                      _focus={{
                        borderColor: "purple.500",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                      }}
                      fontWeight="500"
                      fontSize="xs"
                    />
                  </FormControl>

                  <FormControl>
                    <HStack spacing={1} mb={1} align="center">
                      <Icon as={PiPresentationFill} boxSize={4} color="purple.700" />
                      <Text fontSize="xs" fontWeight="700" color="gray.700" mb={0}>
                        Reporting Instructions
                      </Text>
                      <Tooltip
                        label="How should the alert be presented? Specify columns, ranking, and narrative format."
                        placement="top"
                        hasArrow
                        fontSize="xs"
                        maxW="220px"
                        bg="blue.50"
                        color="gray.700"
                        py={2}
                        px={2}
                        borderRadius="md"
                        fontWeight="600"
                      >
                        <span>
                          <Icon as={LuInfo} boxSize={4} color="blue.300" cursor="help" />
                        </span>
                      </Tooltip>
                    </HStack>
                    <Textarea
                      size="sm"
                      value={reportingInstructions}
                      onChange={handleFieldChange(setReportingInstructions)}
                      placeholder="e.g. Table of top 10 products by sales, include sales delta and price change %…"
                      rows={9}
                      resize="none"
                      _focus={{
                        borderColor: "purple.500",
                        boxShadow: "0 0 0 1px var(--chakra-colors-purple-500)",
                      }}
                      fontWeight="500"
                      fontSize="xs"
                    />
                  </FormControl>
                </SimpleGrid>
              </VStack>
            )}

            {/* ── NOTES TAB ── */}
            {activeTab === "notes" && (
              <VStack spacing={3} align="stretch">
                {/* Analysis Instructions – code-style, locked */}
                <Box
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="md"
                  overflow="hidden"
                >
                  <HStack
                    px={2}
                    py={0}
                    bg="white"
                    justifyContent="space-between"
                    borderBottom="1px solid"
                    borderColor="gray.700"
                  >
                    <HStack spacing={2}>
                      <Text
                        fontSize="xs"
                        color="gray.700"
                        fontFamily="mono"
                        ml={1}
                        mb={0}
                        fontWeight="600"
                      >
                        Instructions to self
                      </Text>
                      <Text fontSize="xs" color="gray.700" mb={0} fontFamily="mono">{notesLocked ? "(locked — click 🔒 to edit)" : "(editing...)"}</Text>
                    </HStack>
                    <Tooltip
                      label={notesLocked ? "Unlock to edit" : "Lock editing"}
                      placement="top"
                      hasArrow
                      fontSize="xs"
                    >
                      <Button
                        size="xs"
                        variant="ghost"
                        leftIcon={
                          <Icon
                            as={notesLocked ? FaLock : FaUnlock}
                            boxSize={3}
                          />
                        }
                        aria-label={notesLocked ? "Unlock notes" : "Lock notes"}
                        color={notesLocked ? "gray.500" : "purple.600"}
                        _hover={{
                          color: notesLocked ? "gray.700" : "purple.700",
                          bg: "purple.50",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setNotesLocked((prev) => !prev);
                        }}
                      >
                        {notesLocked ? "Edit" : "Editing"}
                      </Button>
                    </Tooltip>
                  </HStack>

                  <Textarea
                    value={analysisInstructions}
                    onChange={handleFieldChange(setAnalysisInstructions)}
                    placeholder="# agent analysis notes&#10;# how to build the data query…"
                    rows={10}
                    resize="none"
                    isReadOnly={notesLocked}
                    fontFamily="'JetBrains Mono', 'Fira Code', 'Cascadia Code', 'Source Code Pro', monospace"
                    fontSize="xs"
                    lineHeight="1.6"
                    bg={notesLocked ? "gray.50" : "white"}
                    color={notesLocked ? "gray.700" : "gray.800"}
                    borderRadius="none"
                    border="none"
                    _focus={{
                      border: "none",
                      boxShadow: "none",
                    }}
                    _readOnly={{
                      cursor: "default",
                      opacity: 1,
                      bg: "gray.50",
                      color: "gray.700",
                    }}
                    p={3}
                    sx={{
                      caretColor: "green.300",
                      "::placeholder": {
                        color: "gray.600",
                        fontFamily:
                          "'JetBrains Mono', 'Fira Code', monospace",
                      },
                    }}
                  />
                </Box>
              </VStack>
            )}

            {/* ── SCHEDULE TAB ── */}
            {activeTab === "schedule" && (
              <VStack
                spacing={3}
                align="center"
                justify="center"
                py={8}
                color="gray.400"
              >
                <Icon as={LuCalendarDays} boxSize={8} />
                <VStack spacing={1}>
                  <Text fontSize="sm" fontWeight="600" color="gray.500" mb={0}>
                    Scheduling coming soon
                  </Text>
                  <Text fontSize="xs" color="gray.400" mb={0} textAlign="center">
                    Configure when this scenario alert should automatically run.
                  </Text>
                </VStack>
                <Box
                  px={3}
                  py={1.5}
                  bg="purple.50"
                  borderRadius="md"
                  border="1px dashed"
                  borderColor="purple.200"
                >
                  <Text fontSize="xs" color="purple.500" fontWeight="500" mb={0}>
                    TODO
                  </Text>
                </Box>
              </VStack>
            )}
          </Box>

          {/* ── Footer Actions ── */}
          <HStack
            px={2}
            pt={1}
            pb={2}
            bg="white"
            justify="end"
          >
            {/* Save / Discard on the right */}
            <HStack spacing={2}>
              {isChanged && (
                <Button
                  size="xs"
                  colorScheme="red"
                  variant="outline"
                  leftIcon={<Icon as={FaUndo} boxSize={3} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDiscard();
                  }}
                  isDisabled={isSaving}
                  fontWeight="500"
                >
                  Discard Changes
                </Button>
              )}
              <Button
                size="xs"
                colorScheme="green"
                leftIcon={<Icon as={FaCheck} boxSize={3} />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSave();
                }}
                isLoading={isSaving}
                fontWeight="500"
              >
                <Text fontSize="xs" fontWeight="700" mb={0}>
                  {isInCurrentAnalyst ? "Update" : "Save"}
                </Text>
              </Button>
            </HStack>
          </HStack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default ScenarioArtifactCard;
